import { Button, Grid } from "@mui/material";
import ProviderRegistry from "./ProviderRegistry";

/**
 * Renders a provider button component based on the provider type
 * @param {string} provider - The provider type (from SupportedAuthProviders)
 * @param {Object} props - Button props and configuration
 * @returns {JSX.Element} - The rendered provider button
 */
export const renderProviderButton = (provider, {
  isSelected,
  enabledProvidersCount,
  onMethodSelect,
  theme,
  primaryGradient,
  localizationProvider
}) => {
  const config = ProviderRegistry.getConfig(provider);
  
  if (!config) {
    console.warn(`Unknown provider: ${provider}`);
    return null;
  }

  const IconComponent = config.icon;

  return (
    <Grid 
      key={provider} 
      item 
      xs={enabledProvidersCount === 1 ? 12 : 6}
    >
      <Button
        fullWidth
        variant={isSelected ? 'contained' : 'outlined'}
        onClick={() => onMethodSelect(provider)}
        startIcon={<IconComponent sx={{ mx: 0.5 }} />}
        sx={{
          py: 1.5,
          borderRadius: 3,
          fontSize: '0.95rem',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.3s ease',
          ...(isSelected ? {
            background: primaryGradient,
            boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
            }
          } : {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              transform: 'translateY(-2px)',
              borderColor: theme.palette.primary.dark,
              backgroundColor: `${theme.palette.primary.main}10`
            }
          })
        }}
      >
        {localizationProvider.translateByType({
          type: "Internal",
          text: config.translationKey
        }) || config.fallbackText}
      </Button>
    </Grid>
  );
};

/**
 * Renders all enabled provider buttons
 * @param {Array} enabledProviders - Array of enabled provider types
 * @param {Object} props - Common props for all buttons
 * @returns {Array} - Array of rendered provider buttons
 */
export const renderProviderButtons = (enabledProviders, {
  selectedProvider,
  onMethodSelect,
  theme,
  primaryGradient,
  localizationProvider
}) => {
  // Filter to only include registered providers
  const registeredProviders = ProviderRegistry.filterRegisteredProviders(enabledProviders);
  
  return registeredProviders.map(provider => 
    renderProviderButton(provider, {
      isSelected: selectedProvider === provider,
      enabledProvidersCount: registeredProviders.length,
      onMethodSelect,
      theme,
      primaryGradient,
      localizationProvider
    })
  );
};

/**
 * Gets the configuration for a specific provider
 * @param {string} provider - The provider type
 * @returns {Object|null} - The provider configuration or null if not found
 */
export const getProviderConfig = (provider) => {
  return ProviderRegistry.getConfig(provider);
};

/**
 * Checks if a provider is supported
 * @param {string} provider - The provider type to check
 * @returns {boolean} - True if the provider is supported
 */
export const isProviderSupported = (provider) => {
  return ProviderRegistry.isRegistered(provider);
};

/**
 * Gets all supported provider types
 * @returns {Array} - Array of all supported provider types
 */
export const getSupportedProviders = () => {
  return ProviderRegistry.getAllProviders();
};

const ProviderComponentMapper = {
  renderProviderButton,
  renderProviderButtons,
  getProviderConfig,
  isProviderSupported,
  getSupportedProviders
};

export default ProviderComponentMapper;